import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Pagination,
  Stack
} from '@mui/material';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs } from '@firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { UserProfile } from '../../types/firebase';
import { respondToFriendRequest } from '../../services/friendService';

interface SenderProfile {
  id: string;
  name: string;
  photoURL?: string;
  displayName?: string;
}

interface FriendRequestsProps {
  onRequestAccepted?: () => void;
}

const ITEMS_PER_PAGE = 5; // Show 5 friend requests per page

const FriendRequests: React.FC<FriendRequestsProps> = ({ onRequestAccepted }) => {
  const [receivedRequests, setReceivedRequests] = useState<string[]>([]);
  const [senderProfiles, setSenderProfiles] = useState<{ [key: string]: SenderProfile }>({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { currentUser, setUserProfile, userProfile } = useAuth();

  const loadSenderProfile = async (senderId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', senderId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: senderId,
          name: userData.displayName || userData.name || 'Unknown User',
          photoURL: userData.photoURL || userData.avatar,
          displayName: userData.displayName
        };
      }
    } catch (error) {
      console.error('Error loading sender profile:', error);
    }
    return null;
  };

  const loadFriendRequests = React.useCallback(async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserProfile;
        const requests = userData.friendRequests?.received || [];
        setReceivedRequests(requests);

        // Fetch all sender profiles
        const profiles: { [key: string]: SenderProfile } = {};
        for (const senderId of requests) {
          const profile = await loadSenderProfile(senderId);
          if (profile) {
            profiles[senderId] = profile;
          }
        }
        setSenderProfiles(profiles);
      }
    } catch (error) {
      console.error('Error loading friend requests:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadFriendRequests();
  }, [loadFriendRequests]);

  // Reset to first page when requests change
  useEffect(() => {
    setCurrentPage(1);
  }, [receivedRequests.length]);

  const handleResponse = async (senderId: string, response: 'accepted' | 'rejected') => {
    if (!currentUser) return;
    try {
      // Find the friend request document in the collection
      const q = query(
        collection(db, 'friendRequests'),
        where('senderId', '==', senderId),
        where('receiverId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Use the service function to handle the response properly
        const requestId = querySnapshot.docs[0].id;
        await respondToFriendRequest(requestId, response, currentUser.uid);
      } else {
        // Fallback: handle the case where the request might only exist in user arrays
        if (response === 'accepted') {
          // Add each user to the other's friends list and remove the request
          await Promise.all([
            // Update current user's document
            updateDoc(doc(db, 'users', currentUser.uid), {
              friends: arrayUnion(senderId),
              'friendRequests.received': arrayRemove(senderId)
            }),
            // Update sender's document
            updateDoc(doc(db, 'users', senderId), {
              friends: arrayUnion(currentUser.uid),
              'friendRequests.sent': arrayRemove(currentUser.uid)
            })
          ]);
          
          // Notify parent component that a request was accepted
          onRequestAccepted?.();
        } else {
          // Just remove the request
          await Promise.all([
            updateDoc(doc(db, 'users', currentUser.uid), {
              'friendRequests.received': arrayRemove(senderId)
            }),
            updateDoc(doc(db, 'users', senderId), {
              'friendRequests.sent': arrayRemove(currentUser.uid)
            })
          ]);
        }
      }
      
      // Refresh the requests list
      loadFriendRequests();
      // Update userProfile in AuthContext for instant badge update
      if (userProfile && userProfile.friendRequests) {
        setUserProfile({
          ...userProfile,
          friendRequests: {
            ...userProfile.friendRequests,
            received: userProfile.friendRequests.received.filter(id => id !== senderId)
          }
        });
      }
    } catch (error) {
      console.error('Error responding to friend request:', error);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Calculate pagination
  const totalPages = Math.ceil(receivedRequests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentRequests = receivedRequests.slice(startIndex, endIndex);

  if (loading) {
    return <Typography>Loading friend requests...</Typography>;
  }

  if (receivedRequests.length === 0) {
    return null;
  }

  return (
    <Box sx={{ backgroundColor: 'rgba(30, 30, 30, 0.9)', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Friend Requests ({receivedRequests.length})
        </Typography>
      </Box>
      <List sx={{ p: 0 }}>
        {currentRequests.map((senderId) => {
          const senderProfile = senderProfiles[senderId];
          return (
            <ListItem
              key={senderId}
              sx={{
                p: 2,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:last-child': {
                  borderBottom: 'none'
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={senderProfile?.photoURL}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: '#4a90e2',
                    color: '#fff',
                    fontSize: '1.2rem'
                  }}
                >
                  {senderProfile?.name?.[0]?.toUpperCase() || senderId[0]?.toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                    {senderProfile?.displayName || senderProfile?.name || 'New Friend Request'}
                  </Typography>
                }
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleResponse(senderId, 'accepted')}
                  sx={{
                    backgroundColor: '#4CAF50',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    },
                    minWidth: 'unset',
                    px: 2
                  }}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleResponse(senderId, 'rejected')}
                  sx={{
                    color: '#ff5252',
                    borderColor: '#ff5252',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 82, 82, 0.1)',
                      borderColor: '#ff5252'
                    },
                    minWidth: 'unset',
                    px: 2
                  }}
                >
                  Decline
                </Button>
              </Box>
            </ListItem>
          );
        })}
      </List>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
            <Typography variant="body2" sx={{ color: '#fff' }}>
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#fff',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }
              }}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FriendRequests; 